import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  LogOut,
  Phone,
  Calendar as CalIcon,
  Download,
  GraduationCap,
  MessageCircle,
  Clock,
  StickyNote,
  Eye,
  Trash2,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { hasAdminAccess } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { useAdminSearch, matchesQuery } from "@/components/admin/AdminSearchContext";
import { RescheduleBadge } from "@/components/admin/RescheduleBadge";
import { listRecentDateChangesByPhone, type DateChangeBadge } from "@/lib/student-lifecycle.functions";
import { buildWhatsAppUrl, waDigits } from "@/lib/phone";
import { useServerFn } from "@tanstack/react-start";
import { CHECKOUT_STARTED_TAG } from "@/lib/pending-enrollment";
import { checkoutFollowUpMessage } from "@/lib/checkout-followup";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

type Lead = {
  id: string;
  name: string;
  phone: string;
  plan_key: "basic" | "full" | "refusal";
  university: string | null;
  interview_date: string | null;
  status: "new" | "contacted" | "enrolled" | "lost";
  notes: string | null;
  created_at: string;
};

const PLAN_LABEL: Record<Lead["plan_key"], string> = {
  basic: "Basic",
  full: "Full Support",
  refusal: "Refusal Support",
};

const STATUS_COLOR: Record<Lead["status"], string> = {
  new: "bg-primary/10 text-primary border-primary/20",
  contacted: "bg-amber-100 text-amber-900 border-amber-200",
  enrolled: "bg-emerald-100 text-emerald-900 border-emerald-200",
  lost: "bg-muted text-muted-foreground border-border",
};

export const Route = createFileRoute("/admin/leads")({
  head: () => ({
    meta: [{ title: "Enrollment leads - Admin" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminLeadsPage,
});

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return "";
  let s = String(value);
  // Neutralize spreadsheet formula injection.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportCsv(rows: Lead[]) {
  const headers = [
    "Name",
    "Phone",
    "Plan",
    "University",
    "Interview Date",
    "Status",
    "Notes",
    "Created At",
  ];
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.name,
        r.phone,
        PLAN_LABEL[r.plan_key] ?? r.plan_key,
        r.university ?? "",
        r.interview_date ?? "",
        r.status,
        r.notes ?? "",
        r.created_at,
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `enrollment-leads-${format(new Date(), "yyyy-MM-dd-HHmm")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function AdminLeadsPage() {
  const navigate = useNavigate();
  const { query } = useAdminSearch();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [planFilter, setPlanFilter] = useState<"all" | Lead["plan_key"]>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | Lead["status"]>("all");
  const [dateFilter, setDateFilter] = useState<"all" | "7" | "30" | "90">("all");
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const [changesByPhone, setChangesByPhone] = useState<Record<string, DateChangeBadge>>({});
  const changesFn = useServerFn(listRecentDateChangesByPhone);


  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("enrollment_leads")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Couldn't load leads");
      return;
    }
    setLeads((data ?? []) as Lead[]);
    try {
      const ch = await changesFn({});
      setChangesByPhone(ch.byPhone ?? {});
    } catch {
      /* badges are best-effort */
    }
  }, [changesFn]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate({ to: "/auth" });
        return;
      }
      if (!active) return;
      if (!(await hasAdminAccess(sessionData.session))) {
        toast.error("This account is not an admin yet.");
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      await load();
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [navigate, load]);

  const updateStatus = async (id: string, status: Lead["status"]) => {
    const prev = leads;
    setLeads((s) => s.map((row) => (row.id === id ? { ...row, status } : row)));
    const { error } = await supabase.from("enrollment_leads").update({ status }).eq("id", id);
    if (error) {
      setLeads(prev);
      toast.error("Couldn't update status");
    } else {
      toast.success("Status updated");
    }
  };

  const deleteLead = async (l: Lead) => {
    if (!window.confirm(`Revoke enrollment lead from ${l.name}?\nThis cannot be undone.`)) return;
    const prev = leads;
    setLeads((s) => s.filter((row) => row.id !== l.id));
    const { error } = await supabase.from("enrollment_leads").delete().eq("id", l.id);
    if (error) {
      setLeads(prev);
      toast.error("Couldn't revoke lead");
    } else {
      toast.success("Lead revoked");
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  const filtered = useMemo(() => {
    const cutoffMs =
      dateFilter === "all" ? null : Date.now() - parseInt(dateFilter, 10) * 24 * 60 * 60 * 1000;
    return leads.filter(
      (l) =>
        (planFilter === "all" || l.plan_key === planFilter) &&
        (statusFilter === "all" || l.status === statusFilter) &&
        (cutoffMs === null || new Date(l.created_at).getTime() >= cutoffMs) &&
        matchesQuery(query, l.name, l.phone, l.university, PLAN_LABEL[l.plan_key], l.notes),
    );
  }, [leads, planFilter, statusFilter, dateFilter, query]);


  const counts = {
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    enrolled: leads.filter((l) => l.status === "enrolled").length,
    lost: leads.filter((l) => l.status === "lost").length,
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl">Not authorized</h1>
          <p className="mt-2 text-muted-foreground">Your account doesn't have admin access yet.</p>
          <div className="mt-4 flex justify-center gap-3">
            <Button variant="outline" onClick={handleSignOut}>
              Sign out
            </Button>
            <Link to="/" className="inline-flex items-center px-4 py-2 rounded-md text-sm">
              Go home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminShell
      title="Enrollment leads"
      description={
        <>
          {counts.new} new · {counts.contacted} contacted · {counts.enrolled} enrolled · {counts.lost} lost{" · "}
          <Link to="/admin/signups" className="hover:underline">View group signups</Link>
        </>
      }
      actions={
        <>
          <Select value={planFilter} onValueChange={(v) => setPlanFilter(v as typeof planFilter)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Plan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              <SelectItem value="basic">Basic</SelectItem>
              <SelectItem value="full">Full Support</SelectItem>
              <SelectItem value="refusal">Refusal Support</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="enrolled">Enrolled</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>
          <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as typeof dateFilter)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Date" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => exportCsv(filtered)} disabled={filtered.length === 0} className="gap-2">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
          <Button variant="outline" onClick={handleSignOut} className="gap-2">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </>
      }
    >

      <div className="mt-8 grid gap-3">
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            No leads match these filters.
          </div>
        )}
        {filtered.map((l) => (
          <article
            key={l.id}
            onClick={() => setActiveLead(l)}
            className="rounded-2xl border border-border bg-card p-5 cursor-pointer hover:border-primary/40 transition"
          >

            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg font-semibold">{l.name}</h3>
                  <Badge variant="outline" className={STATUS_COLOR[l.status]}>
                    {l.status}
                  </Badge>
                  <Badge variant="secondary">{PLAN_LABEL[l.plan_key]}</Badge>
                  {l.notes?.includes(CHECKOUT_STARTED_TAG) && (
                    <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900">
                      Left at sign-in
                    </Badge>
                  )}
                  <RescheduleBadge change={changesByPhone[waDigits(l.phone).slice(-10)]} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <a
                    href={buildWhatsAppUrl(l.phone, `Hi ${l.name || "there"}! This is the WeLivdIt team.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" /> {l.phone}
                  </a>
                  {l.status !== "enrolled" && waDigits(l.phone) && (
                    <a
                      href={buildWhatsAppUrl(l.phone, checkoutFollowUpMessage(l.name, l.plan_key))}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-semibold text-white hover:opacity-90"
                    >
                      <MessageCircle className="h-3.5 w-3.5" /> Payment follow-up
                    </a>
                  )}
                  {l.university && (
                    <span className="inline-flex items-center gap-1">
                      <GraduationCap className="h-3.5 w-3.5" /> {l.university}
                    </span>
                  )}
                  {l.interview_date && (
                    <span className="inline-flex items-center gap-1">
                      <CalIcon className="h-3.5 w-3.5" /> Interview{" "}
                      {format(new Date(l.interview_date), "PP")}
                    </span>
                  )}
                  <span>Submitted {format(new Date(l.created_at), "PP p")}</span>
                </div>
                {l.notes && (
                  <p className="mt-2 text-sm text-foreground/80 whitespace-pre-wrap">{l.notes}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-2" onClick={(e) => e.stopPropagation()}>
                <Select
                  value={l.status}
                  onValueChange={(v) => updateStatus(l.id, v as Lead["status"])}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="enrolled">Enrolled</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setActiveLead(l)}
                  className="gap-1.5"
                >
                  <Eye className="h-3.5 w-3.5" /> Details
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteLead(l)}
                  className="gap-1.5 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Revoke
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>

      <LeadDetailsDrawer lead={activeLead} onClose={() => setActiveLead(null)} />
    </AdminShell>
  );
}

function buildLeadWhatsApp(l: Lead): string {
  const planLabel = PLAN_LABEL[l.plan_key] ?? l.plan_key;
  const lines = [
    `Hi ${l.name}! 👋 This is the WeLivdIt team reaching out about your *${planLabel}* enrollment.`,
    ``,
    l.interview_date
      ? `We saw your visa interview is on ${format(new Date(l.interview_date), "PPP")} - let's get your prep locked in.`
      : `Let's get your visa prep schedule locked in.`,
    l.university ? `\nUniversity on file: ${l.university}` : "",
    ``,
    `Quick questions to confirm:`,
    `1. OFC (Biometrics) date and city?`,
    `2. Visa interview city?`,
    `3. Course you're going for?`,
    `4. Best time for your first mock session this week?`,
  ].filter(Boolean);
  return lines.join("\n");
}

function LeadDetailsDrawer({ lead, onClose }: { lead: Lead | null; onClose: () => void }) {
  if (!lead) {
    return (
      <Sheet
        open={false}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
      >
        <SheetContent />
      </Sheet>
    );
  }
  const phoneDigits = waDigits(lead.phone);
  const waUrl = buildWhatsAppUrl(lead.phone, buildLeadWhatsApp(lead));
  return (
    <Sheet
      open={!!lead}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <SheetTitle className="font-display text-2xl">{lead.name}</SheetTitle>
            <Badge variant="outline" className={STATUS_COLOR[lead.status]}>
              {lead.status}
            </Badge>
          </div>
          <SheetDescription className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary">{PLAN_LABEL[lead.plan_key]}</Badge>
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Submitted {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
            </span>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5 text-sm">
          <DetailRow icon={<Phone className="h-4 w-4 text-primary" />} label="Phone">
            <a href={`tel:${lead.phone}`} className="text-foreground hover:underline">
              {lead.phone}
            </a>
          </DetailRow>

          <DetailRow icon={<GraduationCap className="h-4 w-4 text-primary" />} label="University">
            {lead.university || <span className="text-muted-foreground">Not provided</span>}
          </DetailRow>

          <DetailRow icon={<CalIcon className="h-4 w-4 text-primary" />} label="Interview date">
            {lead.interview_date ? (
              format(new Date(lead.interview_date), "PPPP")
            ) : (
              <span className="text-muted-foreground">Not provided</span>
            )}
          </DetailRow>

          <DetailRow icon={<Clock className="h-4 w-4 text-primary" />} label="Created">
            {format(new Date(lead.created_at), "PPP 'at' p")}
          </DetailRow>

          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
              <StickyNote className="h-3.5 w-3.5" /> Notes
            </div>
            {lead.notes ? (
              <div className="rounded-xl border border-border bg-muted/30 p-3 text-sm whitespace-pre-wrap text-foreground/90">
                {lead.notes}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No notes left by the lead.</p>
            )}
          </div>
        </div>

        <div className="mt-8 sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-background border-t border-border">
          <a
            href={waUrl}
            target="_blank"
            rel="noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-white text-sm font-semibold hover:opacity-90 transition"
          >
            <MessageCircle className="h-4 w-4" />
            Message {lead.name.split(" ")[0]} on WhatsApp
          </a>
          <p className="mt-2 text-[10px] text-center text-muted-foreground">
            Opens WhatsApp with a prefilled intro using their plan, interview date and university.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}
